// DealNewPage — форма создания сделки (R17a, D14).
// После успешного create → редирект на /crm/deals/{id}.
// R30 / U-14: департамент — обязательный явный выбор (DepartmentAutocomplete, U-20:
// поисковый селект вместо 1258 MenuItem в DOM).
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, MenuItem, Stack, TextField } from '@mui/material';
import { PageHeader } from '@/theme/PageHeader';
import { DatePickerField } from '@/components/forms';
import { dealsApi, DEAL_CURRENCIES, type DealCurrency, type DealStage } from '@/api/deals';
import { useContactsPage } from '@/api/contacts';
import { useCompanyMap } from '@/api/companies';
import { useAuth } from '@/auth/AuthContext';
import { extractApiErrorMessage } from '@/lib/apiError';
import { normalizeAmountInput, parseAmount, amountSchema } from '@/lib/money';
import { t } from '@/i18n';
import { UserAutocomplete } from '@/components/UserAutocomplete';
import { DepartmentAutocomplete } from '@/components/DepartmentAutocomplete';

/** true если дата (yyyy-MM-dd) не задана или сегодня/позже (U-17). */
function isTodayOrFuture(v: string | undefined): boolean {
  return !v || new Date(v).getTime() >= new Date(new Date().toDateString()).getTime();
}

/** Zod-схема валидации формы создания сделки. */
const dealFormSchema = z.object({
  title: z.string().min(1, 'Название обязательно').max(255, 'Слишком длинное название'),
  // U-32: amount — единый модуль money.ts (normalizeAmountInput + amountSchema):
  // пусто → «Укажите сумму», не число → «Укажите сумму числом», 0/отрицательное →
  // «Сумма должна быть больше 0» (см. Открытый вопрос №1 — Q17).
  amount: amountSchema,
  currency: z.enum(DEAL_CURRENCIES),
  stage: z.enum(['NEW', 'QUALIFIED', 'NEGOTIATION', 'PROPOSAL', 'WON', 'LOST']),
  companyId: z.string().optional().or(z.literal('')),
  contactId: z.string().optional().or(z.literal('')),
  ownerUserId: z.string().min(1, 'Владелец обязателен'),
  // U-14: департамент обязателен (backend DealCreateRequest.departmentId — NOT NULL).
  departmentId: z.string().min(1, t('crm.deals.department.required')),
  // U-17: dueDate (yyyy-MM-dd, DatePickerField) не может быть в прошлом —
  // валидатор в форме (ручной ввод проходил мимо minDate пикера).
  dueDate: z.string().optional().or(z.literal('')).refine(isTodayOrFuture, t('crm.deals.dueDatePast')),
  description: z.string().max(4000, 'Слишком длинное описание').optional().or(z.literal('')),
});

type DealFormValues = z.infer<typeof dealFormSchema>;

const CURRENCY_OPTIONS: DealCurrency[] = [...DEAL_CURRENCIES];

const STAGE_OPTIONS: Array<{ value: DealStage; label: string }> = [
  { value: 'NEW', label: 'Новая' },
  { value: 'QUALIFIED', label: 'Квалифицирована' },
  { value: 'NEGOTIATION', label: 'Переговоры' },
  { value: 'PROPOSAL', label: 'Предложение' },
  { value: 'WON', label: 'Выиграна' },
  { value: 'LOST', label: 'Проиграна' },
];

/** Страница создания сделки. */
export function DealNewPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  // R26 / баг D26: справочники — shared hooks R23 (size=100; 500 → HTTP 500 на backend).
  const companyMap = useCompanyMap();
  const contactsPage = useContactsPage();

  const { control, handleSubmit, watch } = useForm<DealFormValues>({
    resolver: zodResolver(dealFormSchema),
    defaultValues: {
      title: '',
      // U-32: сумма стартует пустой (не 0) — «0 не прилипает».
      amount: '',
      currency: 'RUB',
      stage: 'NEW',
      companyId: '',
      contactId: '',
      ownerUserId: currentUser?.id ?? '',
      // U-14: дефолт — первый департамент текущего пользователя; иначе пусто + validation error.
      departmentId: currentUser?.departments[0]?.id ?? '',
      dueDate: '',
      description: '',
    },
  });

  const selectedCompanyId = watch('companyId');
  const contactOptions = (contactsPage?.content ?? []).filter(
    (c) => !selectedCompanyId || c.companyId === selectedCompanyId,
  );

  // U-24: ref-флаг закрывает окно «двойной клик/Enter до ре-рендера isPending=true» —
  // useMutation отдаёт snapshot (useSyncExternalStore), в замыкании isPending не виден
  // до ререндера; кнопка disabled — вторая линия защиты.
  const submittingRef = useRef(false);
  const mutation = useMutation({
    mutationFn: (values: DealFormValues) =>
      dealsApi.create({
        title: values.title,
        // U-32: схема гарантирует конечное > 0 — parseAmount даёт число в payload.
        amount: parseAmount(values.amount) as number,
        currency: values.currency,
        stage: values.stage,
        companyId: values.companyId || null,
        contactId: values.contactId || null,
        ownerUserId: values.ownerUserId,
        // U-14: департамент — явное поле формы (обязательное).
        departmentId: values.departmentId,
        dueDate: values.dueDate || null,
        description: values.description || null,
      }),
    onSuccess: (deal) => {
      void queryClient.invalidateQueries({ queryKey: ['deals'] });
      navigate(`/crm/deals/${deal.id}`);
    },
    // U-24: после завершения мутации ref сбрасывается — форма снова submit-доступна.
    onSettled: () => {
      submittingRef.current = false;
    },
  });

  const onSubmit: SubmitHandler<DealFormValues> = (values) => {
    if (submittingRef.current || mutation.isPending) return;
    submittingRef.current = true;
    mutation.mutate(values);
  };

  return (
    <Box>
      <PageHeader title="Новая сделка" subtitle="Заполните обязательные поля и сохраните" />
      {mutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractApiErrorMessage(mutation.error, t('crm.deals.createError'))}
        </Alert>
      ) : null}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={2}>
          <Controller
            name="title"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Название сделки"
                required
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Название сделки' }}
              />
            )}
          />
          <Controller
            name="amount"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                // U-32: нормализация на лету (020000 → 20000, 00.5 → 0.5).
                onChange={(e) => field.onChange(normalizeAmountInput(e.target.value))}
                label="Сумма"
                required
                fullWidth
                type="number"
                placeholder="0,00"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Сумма', min: 0 }}
              />
            )}
          />
          <Controller
            name="currency"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                required
                fullWidth
                label="Валюта"
                inputProps={{ 'aria-label': 'Валюта' }}
              >
                {CURRENCY_OPTIONS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="stage"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                required
                fullWidth
                label="Стадия"
                inputProps={{ 'aria-label': 'Стадия сделки' }}
              >
                {STAGE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="companyId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Компания"
                inputProps={{ 'aria-label': 'Компания' }}
              >
                <MenuItem value="">—</MenuItem>
                {[...(companyMap ?? [])].map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="contactId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Контакт"
                inputProps={{ 'aria-label': 'Контакт' }}
              >
                <MenuItem value="">—</MenuItem>
                {contactOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.firstName} {c.lastName}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          {/* R36b / U-2: живой поиск владельца вместо статичного select (OwnerUserSelect удалён). */}
          <Controller
            name="ownerUserId"
            control={control}
            render={({ field, fieldState }) => (
              <UserAutocomplete
                label="Владелец"
                value={field.value}
                onChange={field.onChange}
                required
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                aria-label="Владелец сделки"
              />
            )}
          />
          {/* U-14/U-20 (sled-V9): департамент обязателен; поисковый селект вместо
              TextField select — 1258 записей не раскрываются, фильтр по вводу. */}
          <Controller
            name="departmentId"
            control={control}
            render={({ field, fieldState }) => (
              <DepartmentAutocomplete
                label={t('crm.deals.department')}
                value={field.value}
                onChange={field.onChange}
                required
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message ?? undefined}
                aria-label={t('crm.deals.department')}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Описание"
                fullWidth
                multiline
                minRows={3}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Описание' }}
              />
            )}
          />
          <DatePickerField
            name="dueDate"
            control={control}
            label="Плановая дата закрытия"
            minDate="today"
            aria-label="Плановая дата закрытия"
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button type="submit" variant="contained" disabled={mutation.isPending}>
              Создать сделку
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
